import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { HoldButton, Sheet, Toast } from '../components/ui';
import { absTime, when } from '../hooks/format';
import { useMe, useNotificationConfig, useSystem } from '../hooks/queries';

export function System() {
  const s = useSystem();
  const me = useMe();
  const notif = useNotificationConfig();
  const [chatId, setChatId] = useState('');
  const qc = useQueryClient();
  const nav = useNavigate();
  const [toast, setToast] = useState<string | null>(null);
  const [sudo, setSudo] = useState<null | (() => Promise<void>)>(null);
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  // Whether THIS device holds a push subscription (the server only knows the user's channels).
  const [deviceSub, setDeviceSub] = useState<boolean | null>(null);
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setDeviceSub(false);
      return;
    }
    void navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then((sub) => setDeviceSub(!!sub)).catch(() => setDeviceSub(false));
  }, [toast]);
  const run = async (fn: () => Promise<void>, msg: string) => {
    try {
      await fn();
      await qc.invalidateQueries();
      setToast(msg);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SUDO_REQUIRED') setSudo(() => fn);
      else setToast(e instanceof ApiError ? e.message : 'failed');
    }
  };
  const confirmSudo = async () => {
    try {
      await api.post('/api/v1/auth/sudo', { password, totp });
      const fn = sudo;
      setSudo(null);
      setPassword('');
      setTotp('');
      if (fn) await run(fn, 'Done');
    } catch {
      setToast('Re-authentication failed');
    }
  };
  if (!s.data) return <div className="empty">…</div>;
  const d = s.data;
  const engaged = d.kill_switch.engaged === true;
  return (
    <div className="page">
      <h1 className="t-h" style={{ margin: '8px 0 14px' }}>System</h1>
      <div className="card" style={{ borderColor: engaged ? 'var(--live)' : undefined }}>
        <div className="t-cap">Kill switch</div>
        {engaged ? (
          <>
            <div className="t-em" style={{ color: 'var(--live)', marginTop: 4 }}>Engaged {when(d.kill_switch.engaged_at ?? null)} by {d.kill_switch.engaged_by}</div>
            <div className="t-sec muted">{d.kill_switch.reason}</div>
            <p className="t-sec muted">Every narrative is halted and open orders were cancelled. Clearing needs re-authentication and does not resume narratives.</p>
            <button className="btn block" onClick={() => void run(() => api.post('/api/v1/system/unkill').then(() => undefined), 'Kill switch cleared')}>Clear kill switch</button>
          </>
        ) : (
          <>
            <p className="t-sec muted">Halts every narrative and cancels all open orders immediately.</p>
            <HoldButton className="btn danger block" onConfirm={() => void run(() => api.post('/api/v1/system/kill', { reason: 'engaged from the app' }).then(() => undefined), 'KILL SWITCH ENGAGED')}>Hold to engage kill switch</HoldButton>
          </>
        )}
      </div>
      <div className="card">
        <div className="t-cap">Health</div>
        <div className="row list-item"><span className="muted">Worker</span><span className={d.worker.stale ? 'warn' : ''}>{d.worker.last_seen_at ? `seen ${when(d.worker.last_seen_at)}` : 'never seen'}</span></div>
        <div className="row list-item"><span className="muted">Environment</span><span>{d.env}</span></div>
        <div className="row list-item"><span className="muted">AI provider</span><span className={d.modes.ai === 'fixture' ? 'warn' : ''}>{d.modes.ai}</span></div>
        <div className="row list-item"><span className="muted">Market data</span><span className={d.modes.market === 'fixture' ? 'warn' : ''}>{d.modes.market}</span></div>
        <div className="row list-item"><span className="muted">Version</span><span className="mono">{d.app_version} · {d.git_sha.slice(0, 7)}</span></div>
      </div>
      <div className="card">
        <div className="t-cap">Broker accounts</div>
        {d.broker_accounts.length === 0 && <div className="muted t-sec">None. The paper broker account is created on the first PAPER start.</div>}
        {d.broker_accounts.map((a) => <div key={a.id} className="list-item"><div className="row"><span>{a.label}</span><span className={`pill${a.kind === 'LIVE' ? ' live' : ' paper'}`}>{a.provider} · {a.kind}</span></div><div className="t-sec muted">reconciled {a.last_reconciled_at ? when(a.last_reconciled_at) : 'never'}</div></div>)}
      </div>
      {d.freezes.length > 0 && <div className="card" style={{ borderColor: 'var(--warn)' }}><div className="t-cap warn">Execution freezes</div>{d.freezes.map((f) => <div key={f.id} className="list-item t-sec">{f.reason} · {absTime(f.created_at)}</div>)}</div>}
      <div className="card">
        <div className="t-cap">AI spend this month</div>
        {d.ai_spend_month_usd.length === 0 && <div className="muted t-sec">$0.00</div>}
        {d.ai_spend_month_usd.map((x, i) => <div key={i} className="row list-item"><span className="muted t-sec">{x.narrative_id ?? 'system'}</span><span className="num">${Number(x.cost_usd).toFixed(2)}</span></div>)}
      </div>
      <div className="card">
        <div className="t-cap">Notifications</div>
        {notif.data && (
          <>
            <div className="list-item">
              {(() => {
                const serverOn = notif.data.channels.some((c) => c.kind === 'WEB_PUSH' && c.enabled);
                const on = serverOn && deviceSub === true;
                return (
                  <>
                    <div className="row"><span>Web push on this device</span><span className={`pill${on ? ' paper' : ''}`}>{on ? 'enabled' : serverOn && deviceSub === false ? 'other device' : 'off'}</span></div>
                    <div className="t-sec muted">{on ? 'Notifications reach this device.' : 'On iPhone, add LORE to the Home Screen first; push only works for installed web apps.'}</div>
                    {!notif.data.vapid_public_key && <div className="t-sec warn">Server has no VAPID keys (run <span className="mono">lore vapid</span>).</div>}
                    {notif.data.vapid_public_key && !on && deviceSub !== null && <button className="btn" style={{ marginTop: 8 }} onClick={() => void run(() => enablePush(notif.data.vapid_public_key!), 'Push enabled on this device')}>Enable push on this device</button>}
                  </>
                );
              })()}
            </div>
            <div className="list-item">
              <div className="row"><span>Telegram</span><span className="pill">{notif.data.channels.some((c) => c.kind === 'TELEGRAM' && c.enabled) ? 'enabled' : 'off'}</span></div>
              {notif.data.telegram_configured ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input className="input" placeholder="chat id" value={chatId || (notif.data.channels.find((c) => c.kind === 'TELEGRAM')?.telegram_chat_id ?? '')} onChange={(e) => setChatId(e.target.value)} />
                  <button className="btn" onClick={() => void run(() => api.post('/api/v1/notifications/telegram', { chat_id: chatId }).then(() => undefined), 'Telegram updated')}>Save</button>
                </div>
              ) : <div className="t-sec muted">Set TELEGRAM_BOT_TOKEN on the server to enable the fallback channel.</div>}
            </div>
          </>
        )}
      </div>
      <div className="card">
        <div className="t-cap">Account</div>
        <div className="row list-item"><span className="muted">{me.data?.email}</span><button className="btn" onClick={() => void api.post('/api/v1/auth/logout').then(() => nav({ to: '/login' }))}>Sign out</button></div>
      </div>
      <Sheet open={sudo !== null} onClose={() => setSudo(null)} title="Confirm it is you">
        <div className="field"><label>Password</label><input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></div>
        <div className="field"><label>Authenticator code</label><input className="input num" inputMode="numeric" value={totp} onChange={(e) => setTotp(e.target.value)} autoComplete="one-time-code" /></div>
        <button className="btn primary block" onClick={() => void confirmSudo()}>Confirm</button>
      </Sheet>
      <Toast message={toast} onDone={() => setToast(null)} />
    </div>
  );
}

async function enablePush(vapidPublicKey: string): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) throw new Error('Push is not supported in this browser');
  const reg = await navigator.serviceWorker.ready;
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Notification permission was not granted');
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) });
  await api.post('/api/v1/notifications/push/subscribe', sub.toJSON());
}
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
